/**
 * Deterministic mulberry32 PRNG.
 * Persist `state` with encounter saves so reload cannot reroll outcomes.
 */
export interface SeededRng {
  /** Current internal state (serialisable). */
  readonly state: number;
  /** Next float in [0, 1). Advances state. */
  next(): number;
  /** Inclusive integer in [min, max]. */
  nextInt(min: number, max: number): number;
  /** Clone with the same state (does not share mutation). */
  clone(): SeededRng;
}

export function createSeededRng(seed: number): SeededRng {
  let state = seed >>> 0;

  const api: SeededRng = {
    get state() {
      return state;
    },
    next() {
      state = (state + 0x6d2b79f5) >>> 0;
      let r = Math.imul(state ^ (state >>> 15), 1 | state);
      r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
      return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
    },
    nextInt(min: number, max: number) {
      if (!Number.isInteger(min) || !Number.isInteger(max) || max < min) {
        throw new Error(`Invalid nextInt range: [${min}, ${max}]`);
      }
      const span = max - min + 1;
      return min + Math.floor(api.next() * span);
    },
    clone() {
      return createSeededRng(state);
    },
  };

  return api;
}

export function restoreSeededRng(state: number): SeededRng {
  return createSeededRng(state);
}
